import type {
  BaseLedger,
  BaseBudget,
} from '@app/application/report/forecast/types'
import type * as BaseDataResponse from '@app/application/report/forecast/types/api/base-data-response'
import type * as LoadBudget from '@app/application/report/forecast/types/api/load-budget-response'
import type { CreateForecastParams } from '@app/application/report/forecast/types/create-forecast-modal'
import type { SaveParams } from '@app/application/report/forecast/types/save-forecast-modal'
import type {
  AccountNode,
  GroupNode,
  ForecastNode,
} from '@app/application/report/forecast/forecast'

import { Controller } from '@hotwired/stimulus'
import { createApp } from '@app/adapters/create-app'
import { SfLog } from '@app/adapters/logger'

import useI18n from '@app/shared/vue/composables/use-I18n'
import SFetch from '@app/utils/sfetch'

import {
  COLUMN_EDITOR_APPLIED,
  COLUMN_EDITOR_CLOSED,
  COLUMN_EDITOR_OPENED,
  FORECAST_CHANGED,
} from '@app/application/report/column-editor/events'
import type { ForecastChangedDetail } from '@app/application/report/column-editor/events'
import {
  FORECAST_CELL_ATTRIBUTE,
  FORECAST_CELL_SELECTOR,
  reportRowFingerprint,
} from '@app/application/report/forecast/column'
import ClearForecastConfirmModal from '@app/application/report/forecast/components/clear-forecast-confirm-modal.vue'
import CreateForecastModal from '@app/application/report/forecast/components/create-forecast-modal.vue'
import ForecastCellInput from '@app/application/report/forecast/components/forecast-cell-input.vue'
import SaveForecastModal from '@app/application/report/forecast/components/save-forecast-modal.vue'
import { Forecast } from '@app/application/report/forecast/forecast'
import {
  adjustmentType,
  nodeLevel,
  trackForecastEvent,
} from '@app/application/report/forecast/track'
import type { EventProperties } from '@app/application/report/forecast/track'

type ForecastCell = HTMLElement & { forecastNode: AccountNode | GroupNode }

export enum PageHeaderState {
  Initial,
  LoadingBaseData,
  ForecastCreated,
}

const I18n = useI18n()

// Persists (in localStorage, effectively forever — it's fine if it's ever
// cleared) that we've auto-opened the value-editing popover once for this user,
// so we only do it on their first forecast for discoverability.
const DEFAULT_POPOVER_SEEN_KEY = 'reports.forecast.defaultPopoverSeen'

const ADJUSTING_CELL_CLASSES = [
  '!tw-border-2',
  '!tw-border-solid',
  '!tw-border-orange-500',
  'tw-relative',
]

export default class extends Controller {
  static targets = [
    'createModalButton',
    'createModalButtonSpinner',
    'saveBudgetButton',
    'saveBudgetSpinner',
    'saveBudgetSuccess',
    'clearButton',
    'reportTable',
  ]

  private createModalButtonTarget!: HTMLButtonElement
  private createModalButtonSpinnerTarget!: HTMLElement
  private saveBudgetButtonTarget!: HTMLElement
  private saveBudgetSpinnerTarget!: HTMLElement
  private saveBudgetSuccessTarget!: HTMLElement
  private clearButtonTarget!: HTMLElement

  reportId!: number
  forecastSaved: boolean = false
  targetYear!: number
  baseLedger: BaseLedger | null = null
  baseBudget: BaseBudget | null = null
  loadedBudgetId: number | null = null
  // When the forecast was loaded from a saved budget, this carries the original
  // base ledger so re-saving keeps pointing at the source, not the budget.
  private restoredBaseLedgerId: number | null = null
  forecast!: Forecast

  private adjustingCell: HTMLElement | null = null

  // The rows the forecast column on screen was built against — see
  // reportRowFingerprint. Null when there is no column.
  private forecastRowFingerprint: string | null = null

  // The column editor has just replaced the report table, so the forecast column
  // has to be built again on the table that lands (reportTableTargetConnected).
  private replayOnNextTable = false

  // The column editor's drawer is open: the table is showing columns that have
  // not been rendered yet, which is no basis for a new forecast column.
  private columnEditorOpen = false

  // The base data request is on its way, so there is no forecast to work with
  // yet. Held here because what the create button's disabled state answers to is
  // now more than one thing.
  private loadingBaseData = false

  private cellInputApp: ReturnType<typeof createApp> | null = null
  private clearConfirmModalApp: ReturnType<typeof createApp> | null = null
  private createModalApp: ReturnType<typeof createApp> | null = null
  private saveModalApp: ReturnType<typeof createApp> | null = null

  reportTableElement!: HTMLElement
  private reportTableTarget!: HTMLElement

  connect(): void {
    document.addEventListener(COLUMN_EDITOR_OPENED, this.handleColumnEditorOpen)
    document.addEventListener(
      COLUMN_EDITOR_CLOSED,
      this.handleColumnEditorClose
    )
    document.addEventListener(COLUMN_EDITOR_APPLIED, this.handleColumnsApplied)
  }

  reportTableTargetConnected(element: HTMLElement): void {
    this.reportTableElement = element
    this.syncCreateModalButton()

    if (!this.replayOnNextTable) return

    this.replayOnNextTable = false
    this.replayForecastColumn()
  }

  disconnect(): void {
    document.removeEventListener(
      COLUMN_EDITOR_OPENED,
      this.handleColumnEditorOpen
    )
    document.removeEventListener(
      COLUMN_EDITOR_CLOSED,
      this.handleColumnEditorClose
    )
    document.removeEventListener(
      COLUMN_EDITOR_APPLIED,
      this.handleColumnsApplied
    )

    this.stopAdjustingCell()

    this.clearConfirmModalApp?.unmount()
    this.clearConfirmModalApp = null

    this.createModalApp?.unmount()
    this.createModalApp = null

    this.saveModalApp?.unmount()
    this.saveModalApp = null
  }

  // Whether a forecast can be started right now: not while its base data is on
  // its way, and not while the column editor is open — the report is then showing
  // the columns being composed in the drawer rather than the ones it was rendered
  // for, and a forecast column built over that preview would be describing a
  // table that is about to be replaced.
  private syncCreateModalButton(): void {
    this.createModalButtonTarget.disabled =
      this.loadingBaseData || this.columnEditorOpen
  }

  // Whether there is a forecast on the report. Both bases are set when one is
  // created and dropped when it is cleared.
  private get hasForecast(): boolean {
    return this.baseLedger !== null || this.baseBudget !== null
  }

  private handleColumnEditorOpen = (): void => {
    this.columnEditorOpen = true
    this.syncCreateModalButton()

    // The drawer lists the forecast column, and it has no way of knowing about
    // one that was already on the report when it opened.
    this.announceForecast()
  }

  private handleColumnEditorClose = (): void => {
    this.columnEditorOpen = false
    this.syncCreateModalButton()
  }

  private handleColumnsApplied = (): void => {
    if (this.hasForecast) this.replayOnNextTable = true
  }

  private announceForecast(): void {
    const detail: ForecastChangedDetail = {
      year: this.hasForecast ? this.targetYear : null,
    }

    document.dispatchEvent(new CustomEvent(FORECAST_CHANGED, { detail }))
  }

  // Puts the forecast column back on the table the column editor has just
  // rendered. Built again rather than carried across: the values are the
  // forecast's own, and its header cells could not be carried anyway — how many
  // rows the report's header has follows from its columns, which is what was just
  // changed.
  private replayForecastColumn(): void {
    // Whatever was being edited was being edited on the table that has gone.
    this.stopAdjustingCell()

    if (
      reportRowFingerprint(this.reportTableElement) !==
      this.forecastRowFingerprint
    ) {
      // A forecast cell is paired with its row by position, so there is nothing
      // to be done with a table whose rows are not the ones the column was built
      // for: putting the column back would be putting values against accounts
      // they aren't for. The forecast goes instead, and the user is told.
      SfLog.error('The report rows changed under the forecast column', {
        extra: { reportId: this.reportId },
      })
      this.removeForecastColumn()
      alert(I18n.t('common.error.try_again'))
      return
    }

    this.insertForecastColumn()

    // A create sets the bases before awaiting the data they are filled from; its
    // own updateForecastColumn fills these cells when that lands, on this table.
    if (this.loadingBaseData) return

    this.updateForecastColumn()
  }

  openCreateForecastModal(): void {
    this.trackEvent('Add button clicked')

    if (!this.createModalApp) {
      this.setReportId()
      this.createModal()
    }

    window.modalCitizen.openModalPortal('reports/create-forecast-modal', {
      reportId: this.reportId,
      onCreate: (params) => {
        void this.handleCreateForecast(params)
      },
    })
  }

  private trackEvent(action: string, properties: EventProperties = {}): void {
    trackForecastEvent(action, {
      ...this.forecastContextProperties(),
      ...properties,
    })
  }

  private forecastContextProperties(): EventProperties {
    if (!this.baseLedger && !this.baseBudget) return {}

    return {
      base_type: this.baseLedger ? 'actuals' : 'budget',
      base_year: this.baseLedger?.year ?? this.baseBudget!.year,
      target_year: this.targetYear,
    }
  }

  async handleCreateForecast(params: CreateForecastParams): Promise<void> {
    this.setPageHeaderState(PageHeaderState.LoadingBaseData)

    this.targetYear = params.targetYear
    this.baseLedger = params.baseLedger
    this.baseBudget = params.baseBudget
    this.loadedBudgetId = params.loadedBudgetId
    this.restoredBaseLedgerId = null

    this.insertForecastColumn()

    const language = this.reportTableElement.dataset.language ?? 'en'

    try {
      if (this.loadedBudgetId === null) {
        this.forecast = new Forecast(await this.fetchBaseData(), language)
      } else {
        const { baseData, baseLedgerId, budgetAccountValues, adjustments } =
          await this.fetchExistingBudget(this.loadedBudgetId)
        this.forecast = new Forecast(baseData, language)
        // Carry the base ledger forward so re-saving keeps pointing at the
        // source rather than the budget itself.
        this.restoredBaseLedgerId = baseLedgerId
        this.forecast.loadBudget(budgetAccountValues, adjustments)
        // The loaded budget matches what is on disk — clearing it shouldn't
        // prompt to confirm until the user actually changes something.
        this.forecastSaved = true
      }
    } catch (error) {
      this.removeForecastColumn()
      alert(I18n.t('common.error.try_again'))
      throw error
    }

    this.updateForecastColumn()
    this.setPageHeaderState(PageHeaderState.ForecastCreated)
    this.startAdjustingFirstCell()

    this.trackEvent('Created', {
      open_mode: this.loadedBudgetId === null ? 'new' : 'existing',
    })
  }

  private startAdjustingFirstCell(): void {
    if (window.Silverfin.presentationMode) return
    if (this.hasSeenDefaultPopover()) return

    const firstCell = this.adjustableCells()[0]
    if (!firstCell) return

    this.markDefaultPopoverSeen()
    this.startAdjustingCellElement(firstCell)
  }

  private hasSeenDefaultPopover(): boolean {
    try {
      return window.localStorage.getItem(DEFAULT_POPOVER_SEEN_KEY) !== null
    } catch {
      return false
    }
  }

  private markDefaultPopoverSeen(): void {
    try {
      window.localStorage.setItem(
        DEFAULT_POPOVER_SEEN_KEY,
        new Date().toISOString()
      )
    } catch {
      // Ignore storage failures (private mode, quota) — the popover simply
      // opens again next time, which is acceptable.
    }
  }

  private adjustableCells(): ForecastCell[] {
    return Array.from(this.forecastBodyCells()).filter((c) => c.forecastNode)
  }

  // The forecast column's own cells, found by their marker rather than by being
  // last: the column editor's preview cells sit in the same rows (see
  // forecast/column.ts).
  private forecastBodyCells(): NodeListOf<ForecastCell> {
    return this.reportTableElement.querySelectorAll<ForecastCell>(
      `tbody tr ${FORECAST_CELL_SELECTOR}`
    )
  }

  clearForecast(): void {
    if (this.forecastSaved || !this.forecast.hasAdjustments()) {
      this.trackEvent('Cleared')
      this.removeForecastColumn()
      return
    }

    if (!this.clearConfirmModalApp) {
      this.createClearConfirmModal()
    }

    window.modalCitizen.openModalPortal('reports/clear-forecast-confirm', {
      onConfirm: () => {
        this.trackEvent('Cleared')
        this.removeForecastColumn()
      },
    })
  }

  createClearConfirmModal(): void {
    this.clearConfirmModalApp = createApp(ClearForecastConfirmModal)
    this.clearConfirmModalApp.mount(document.createElement('div'))
  }

  private removeForecastColumn(): void {
    this.setPageHeaderState(PageHeaderState.Initial)

    this.baseLedger = null
    this.baseBudget = null
    this.loadedBudgetId = null
    this.restoredBaseLedgerId = null

    this.reportTableElement
      .querySelectorAll(FORECAST_CELL_SELECTOR)
      .forEach((cell) => cell.remove())

    this.forecastRowFingerprint = null

    // Clearing a forecast is the one thing that can happen to the column while
    // the drawer is open, so the list it is in has to hear about it.
    this.announceForecast()
  }

  setPageHeaderState(state: PageHeaderState): void {
    switch (state) {
      case PageHeaderState.LoadingBaseData:
        this.loadingBaseData = true
        this.syncCreateModalButton()
        this.createModalButtonSpinnerTarget.classList.remove('tw-hidden')
        break

      case PageHeaderState.ForecastCreated:
        this.loadingBaseData = false
        this.syncCreateModalButton()
        this.createModalButtonTarget.classList.add('!tw-hidden')

        this.saveBudgetButtonTarget.classList.remove('!tw-hidden')
        this.clearButtonTarget.classList.remove('!tw-hidden')
        break

      case PageHeaderState.Initial:
        this.loadingBaseData = false
        this.syncCreateModalButton()
        this.createModalButtonTarget.classList.remove('!tw-hidden')
        this.createModalButtonSpinnerTarget.classList.add('tw-hidden')

        this.saveBudgetButtonTarget.classList.add('!tw-hidden')
        this.saveBudgetSpinnerTarget.classList.add('tw-hidden')
        this.saveBudgetSuccessTarget.classList.add('tw-hidden')
        this.clearButtonTarget.classList.add('!tw-hidden')
        this.forecastSaved = false
        break
    }
  }

  setReportId(): void {
    this.reportId = Number(this.reportTableElement.dataset.reportTemplateId)
  }

  createModal(): void {
    this.createModalApp = createApp(CreateForecastModal)
    this.createModalApp.mount(document.createElement('div'))
  }

  openSaveForecastModal(): void {
    this.trackEvent('Save button clicked')

    if (!this.saveModalApp) {
      this.createSaveModal()
    }

    window.modalCitizen.openModalPortal('reports/save-forecast-modal', {
      reportId: this.reportId,
      targetYear: this.targetYear,
      presetBudgetId: this.loadedBudgetId,
      onSave: (params) => this.handleSaveForecast(params),
    })
  }

  createSaveModal(): void {
    this.saveModalApp = createApp(SaveForecastModal)
    this.saveModalApp.mount(document.createElement('div'))
  }

  async handleSaveForecast(params: SaveParams): Promise<void> {
    this.saveBudgetButtonTarget.classList.add('!tw-hidden')
    this.saveBudgetSpinnerTarget.classList.remove('tw-hidden')

    const url = window.Silverfin.Routes.report_forecast_save_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      this.reportId
    )

    const entries = this.forecast.getAccountEntriesForSave()
    const adjustments = this.forecast.getAdjustmentsForSave()

    try {
      await SFetch.json(url, {
        method: 'POST',
        body: {
          budget_option: params.budgetOption,
          budget_name: params.budgetName,
          budget_id: params.budgetId,
          target_year: this.targetYear,
          base_ledger_id: this.saveBaseLedgerId(),
          entries: entries.map((e) => ({
            account_id: e.accountId,
            value: e.value,
          })),
          adjustments: adjustments.map((a) => ({
            account_id: a.accountId,
            report_group_id: a.reportGroupId,
            expression: a.expression,
          })),
        },
      })

      this.saveBudgetSuccessTarget.classList.remove('tw-hidden')
      this.forecastSaved = true
      this.trackEvent('Saved', { budget_option: params.budgetOption })
    } catch {
      this.saveBudgetButtonTarget.classList.remove('!tw-hidden')
      alert(I18n.t('common.error.try_again'))
    }

    this.saveBudgetSpinnerTarget.classList.add('tw-hidden')
  }

  // The base ledger the forecast was based on. When loaded from a saved budget,
  // the original reference is reused so re-saving keeps pointing at the real
  // source rather than the budget itself. A forecast based on a budget has no
  // ledger; reload then falls back to the budget's own source ledger.
  private saveBaseLedgerId(): number | null {
    return this.restoredBaseLedgerId ?? this.baseLedger?.id ?? null
  }

  async fetchBaseData(): Promise<BaseDataResponse.Data> {
    const url = window.Silverfin.Routes.report_forecast_base_data_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      this.reportId,
      {
        base_ledger_id: this.baseLedger?.id,
        base_budget_id: this.baseBudget?.id,
        base_budget_year: this.baseBudget?.year,
        ledger_id: window.Silverfin.currentLedgerId(),
      }
    )

    return (await SFetch.json(url)) as BaseDataResponse.Data
  }

  async fetchExistingBudget(budgetId: number): Promise<LoadBudget.Response> {
    const url = window.Silverfin.Routes.report_forecast_load_budget_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      this.reportId,
      {
        budget_id: budgetId,
        target_year: this.targetYear,
        ledger_id: window.Silverfin.currentLedgerId(),
      }
    )

    return (await SFetch.json(url)) as LoadBudget.Response
  }

  insertForecastColumn(): void {
    this.reportTableElement
      .querySelectorAll<HTMLTableRowElement>('thead tr')
      .forEach((row, rowIndex) => {
        row.appendChild(this.buildForecastHeaderCell(rowIndex === 0))
      })

    this.reportTableElement
      .querySelectorAll<HTMLTableRowElement>('tbody tr')
      .forEach((row) => row.appendChild(this.buildForecastBodyCell(row)))

    // What a replay onto a re-rendered table is checked against — see
    // replayForecastColumn.
    this.forecastRowFingerprint = reportRowFingerprint(this.reportTableElement)
  }

  // Reports with a budget or an analytical column have more than one header row;
  // only the first one names the column.
  private buildForecastHeaderCell(named: boolean): HTMLTableCellElement {
    const newCell = document.createElement('th')
    newCell.setAttribute(FORECAST_CELL_ATTRIBUTE, '')
    newCell.classList.add(
      '!tw-border-l-2',
      '!tw-border-l-amber-500',
      '!tw-bg-amber-100',
      '!tw-rounded-tr-none'
    )
    newCell.style.setProperty('border-top-color', 'transparent', 'important')
    newCell.style.setProperty('border-right-color', 'transparent', 'important')
    newCell.style.setProperty('border-bottom-color', 'transparent', 'important')

    if (!named) return newCell

    const wrapper = document.createElement('div')
    wrapper.className = 'tw-flex tw-items-center tw-justify-between tw-gap-2'

    const badge = document.createElement('span')
    badge.textContent = I18n.t('reports.forecast.column_header_label')
    badge.className =
      'tw-rounded tw-px-1.5 tw-py-0.5 tw-text-xs tw-uppercase tw-bg-amber-500 tw-text-white'

    const yearSpan = document.createElement('span')
    yearSpan.textContent = this.targetYear.toString()
    yearSpan.classList.add('!tw-text-amber-800')

    wrapper.appendChild(badge)
    wrapper.appendChild(yearSpan)
    newCell.appendChild(wrapper)

    return newCell
  }

  private buildForecastBodyCell(
    row: HTMLTableRowElement
  ): HTMLTableCellElement {
    const newCell = document.createElement('td')
    newCell.setAttribute(FORECAST_CELL_ATTRIBUTE, '')
    newCell.classList.add('!tw-border-l-2', '!tw-border-l-amber-500')

    if (!this.skipForecastCellStyling(row)) {
      newCell.classList.add('!tw-bg-amber-50/50')
    }

    return newCell
  }

  updateForecastColumn(): void {
    const cells = this.forecastBodyCells()

    const flatNodes = this.forecast.flatNodes()

    cells.forEach((cell, index) => {
      const node = flatNodes[index]

      this.updateForecastCell(cell, node)

      if ('id' in node) {
        cell.dataset.testid = `forecast-cell-${node.type.toLowerCase()}-${
          node.id
        }`
      }

      if (node.isAdjustable) {
        cell.forecastNode = node
        cell.dataset.action = 'click->reports--forecast#startAdjustingCell'
        cell.classList.add('tw-cursor-pointer')
      }
    })
  }

  private updateForecastCell(cell: HTMLElement, node: ForecastNode): void {
    cell.textContent = ''

    const value = this.forecast.formattedValue(node)
    const percentage = this.forecastAdjustmentPercentage(node)
    const absoluteDiff =
      percentage === null ? this.forecastAbsoluteDifference(node) : null

    const changeValue = percentage ?? absoluteDiff

    if (changeValue !== null) {
      const wrapper = document.createElement('div')
      wrapper.className = 'tw-flex tw-items-center tw-justify-end tw-gap-1.5'

      const badge = document.createElement('span')

      if (percentage !== null) {
        const sign = percentage >= 0 ? '+' : ''
        badge.textContent = `${sign}${percentage.toFixed(1)}%`
      } else {
        const sign = changeValue >= 0 ? '+' : ''
        badge.textContent = `${sign}${this.forecast.formatNumber(changeValue)}`
      }

      badge.className = this.badgeColorClasses(node, changeValue)

      const valueSpan = document.createElement('span')
      valueSpan.textContent = value

      wrapper.appendChild(badge)
      wrapper.appendChild(valueSpan)
      cell.appendChild(wrapper)
    } else {
      cell.textContent = value
    }
  }

  private badgeColorClasses(node: ForecastNode, changeValue: number): string {
    const base =
      'tw-rounded tw-px-1 tw-py-px -tw-my-1 tw-text-xs tw-font-medium'
    const green = `${base} tw-bg-sf-success-subtle-2 tw-text-sf-success`
    const red = `${base} tw-bg-sf-danger-subtle-2 tw-text-sf-danger-dark-1`
    const gray = `${base} tw-bg-sf-neutral-subtle-2 tw-text-sf-neutral-dark-1`

    let accountType: string | null = null

    if (node.type === 'Account') {
      accountType = node.accountType
    } else if (node.type === 'Group') {
      accountType = this.dominantAccountType(node)
    }

    if (accountType === 'income') return changeValue > 0 ? green : red
    if (accountType === 'expense') return changeValue > 0 ? red : green

    return gray
  }

  private dominantAccountType(
    group: GroupNode
  ): 'income' | 'expense' | 'asset' | 'liability' | 'equity' | null {
    const counts: Partial<Record<string, number>> = {}

    const walk = (node: ForecastNode): void => {
      if (node.type === 'Account') {
        counts[node.accountType] = (counts[node.accountType] ?? 0) + 1
      } else if (node.type === 'Group') {
        node.children.forEach(walk)
      }
    }

    group.children.forEach(walk)

    let dominant: string | null = null
    let max = 0
    let tie = false

    for (const [type, count] of Object.entries(counts)) {
      if (count! > max) {
        max = count!
        dominant = type
        tie = false
      } else if (count === max) {
        tie = true
      }
    }

    return tie
      ? null
      : (dominant as ReturnType<typeof this.dominantAccountType>)
  }

  private skipForecastCellStyling(row: HTMLTableRowElement): boolean {
    return (
      row.classList.contains('report-template-style-highlight') ||
      row.classList.contains('report-template-style-spacer')
    )
  }

  private forecastAdjustmentPercentage(node: ForecastNode): number | null {
    if (!('adjustmentPercentage' in node)) return null

    const percentage = node.adjustmentPercentage

    if (percentage === null || percentage === 0) {
      return null
    } else {
      return percentage
    }
  }

  private forecastAbsoluteDifference(node: ForecastNode): number | null {
    if (node.type === 'Account') {
      if (node.displayValue === 0 && node.adjustedDisplayValue !== 0) {
        return node.adjustedDisplayValue
      }
    } else if (node.type === 'Group') {
      if (
        node.showValue &&
        node.displayValue === 0 &&
        node.adjustedDisplayValue !== 0
      ) {
        return node.adjustedDisplayValue
      }
    } else if (node.type === 'Value') {
      if (
        node.value === 0 &&
        node.adjustedValue !== null &&
        node.adjustedValue !== 0
      ) {
        return node.adjustedValue
      }
    }

    return null
  }

  startAdjustingCell(event: Event): void {
    event.stopPropagation()
    this.startAdjustingCellElement(event.currentTarget as ForecastCell)
  }

  startAdjustingCellElement(cell: ForecastCell): void {
    if (cell === this.adjustingCell) return

    this.stopAdjustingCell()

    const node = cell.forecastNode
    const savedAdjustmentPercentage = node.adjustmentPercentage

    cell.textContent = ''
    cell.classList.add(...ADJUSTING_CELL_CLASSES)
    cell.classList.remove('tw-cursor-pointer')
    this.adjustingCell = cell

    const mountEl = document.createElement('div')
    mountEl.className =
      'tw-absolute tw-inset-0 tw-flex tw-items-center tw-px-2 tw-bg-white'
    cell.appendChild(mountEl)

    this.cellInputApp = createApp(ForecastCellInput, {
      anchorEl: cell,
      displayValue: node.displayValue,
      adjustedDisplayValue: node.adjustedDisplayValue,
      savedAdjustmentPercentage,
      savedExpression: node.adjustmentExpression,
      onApply: (
        adjustment: number,
        adjustmentPercentage: number | null,
        expression: string | null,
        fromQuickButton: boolean
      ) => {
        this.handleApplyAdjustment(
          node,
          adjustment,
          adjustmentPercentage,
          expression,
          fromQuickButton
        )
      },
      onTab: (
        adjustment: number,
        adjustmentPercentage: number | null,
        expression: string | null,
        backward: boolean
      ) => {
        this.handleApplyAdjustment(
          node,
          adjustment,
          adjustmentPercentage,
          expression,
          false
        )
        this.handleTabToNextCell(cell, backward)
      },
      onTabWithoutChange: (backward: boolean) => {
        this.stopAdjustingCell()
        this.handleTabToNextCell(cell, backward)
      },
      onReset: () => {
        this.handleResetAdjustment(node)
      },
      onClose: () => {
        this.stopAdjustingCell()
      },
    })
    this.cellInputApp.mount(mountEl)
  }

  handleApplyAdjustment(
    node: AccountNode | GroupNode,
    adjustment: number,
    adjustmentPercentage: number | null,
    expression: string | null,
    fromQuickButton: boolean
  ): void {
    this.forecast.applyAdjustment(
      node,
      adjustment,
      adjustmentPercentage,
      expression
    )
    this.markForecastChanged()
    this.stopAdjustingCell()
    this.updateForecastColumn()

    this.trackEvent('Adjustment applied', {
      level: nodeLevel(node),
      adjustment_type: adjustmentType(fromQuickButton, expression),
      percentage:
        adjustmentPercentage === null
          ? null
          : Math.round(adjustmentPercentage * 100) / 100,
    })
  }

  private markForecastChanged(): void {
    if (this.forecastSaved) {
      this.saveBudgetSuccessTarget.classList.add('tw-hidden')
      this.saveBudgetButtonTarget.classList.remove('!tw-hidden')
    }

    this.forecastSaved = false
  }

  handleTabToNextCell(cell: ForecastCell, backward: boolean): void {
    const adjustableCells = this.adjustableCells()

    const currentIndex = adjustableCells.indexOf(cell)
    const nextCell = adjustableCells[currentIndex + (backward ? -1 : 1)]
    if (nextCell) {
      this.trackEvent('Tab to next cell', {
        from_level: nodeLevel(cell.forecastNode),
        direction: backward ? 'backward' : 'forward',
      })
      this.startAdjustingCellElement(nextCell)
    }
  }

  handleResetAdjustment(node: AccountNode | GroupNode): void {
    this.forecast.resetAdjustment(node)
    this.markForecastChanged()
    this.stopAdjustingCell()
    this.updateForecastColumn()

    this.trackEvent('Adjustment reset', {
      level: nodeLevel(node),
    })
  }

  stopAdjustingCell(): void {
    if (!this.adjustingCell) return

    this.cellInputApp!.unmount()
    this.cellInputApp = null

    const cell = this.adjustingCell
    this.adjustingCell = null

    cell.classList.remove(...ADJUSTING_CELL_CLASSES)
    cell.classList.add('tw-cursor-pointer')
    this.updateForecastCell(cell, (cell as ForecastCell).forecastNode)
  }
}
